import { Button, Tooltip, Typography, capitalize, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import type { FC, ReactNode } from 'react';

import { pluralize } from '@core/utils/pluralize';

type Props = {
  selectedRowCount: number;
  rowCount: number;
  headerHeight?: number;
  tableTitle?: string;
  onSelectAllClick: (isChecked: boolean) => void;
  onClickEdit?: () => void;
  hasBulkEditPermission?: boolean;
  bulkActionsSlot?: ReactNode;
};

export const BulkSelectOverlay: FC<Props> = ({
  selectedRowCount,
  rowCount,
  headerHeight,
  onSelectAllClick,
  tableTitle,
  onClickEdit,
  hasBulkEditPermission,
  bulkActionsSlot,
}) => {
  const theme = useTheme();

  const entityTitle = pluralize(selectedRowCount, tableTitle?.toLowerCase() ?? 'item');

  return (
    <Box
      px={0.5}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        left: 0,
        zIndex: 10,
        bgcolor: '#f3f4f6',
        height: headerHeight,
        display: 'flex',
        borderBottom: `1px solid ${theme.palette.SlabGray.Border}`,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box alignItems='center' display='flex'>
        <Checkbox
          color='primary'
          onChange={() => onSelectAllClick(false)}
          indeterminate={selectedRowCount > 0 && selectedRowCount < rowCount}
          checked={rowCount > 0 && selectedRowCount >= rowCount}
          inputProps={{
            'aria-label': 'select all',
          }}
        />
        <Typography
          variant='body2'
          sx={{ ml: 0.5, fontWeight: 500, [theme.breakpoints.down('sm')]: { fontSize: 14 } }}
        >
          {selectedRowCount} {capitalize(entityTitle)} selected
        </Typography>
      </Box>
      <Box alignItems='center' display='flex' gap={1}>
        {bulkActionsSlot}

        {onClickEdit !== undefined && selectedRowCount > 1 && (
          <Tooltip
            title={
              !hasBulkEditPermission ? `You don't have permission to bulk edit ${entityTitle}` : ''
            }
          >
            <Box>
              <Button
                variant='slab-action-bordered'
                disabled={!hasBulkEditPermission}
                onClick={onClickEdit}
              >
                Edit ({selectedRowCount}) {capitalize(entityTitle)}
              </Button>
            </Box>
          </Tooltip>
        )}

        <Box
          sx={{
            ml: 1,
            background: theme.palette.SlabGray['400'],
            height: '44px',
            width: '1px',
          }}
        />
        <Button variant='slab-action-plain' onClick={() => onSelectAllClick(false)}>
          Clear Selection
        </Button>
      </Box>
    </Box>
  );
};
